import { Box, TextField, Button } from "@mui/material";
import { useState } from "react";
import { apiUrlPrefix } from "../../../../config";
import EmojiComponent from "../../discussions/EmojiComponent";
import { Stack } from "@mui/system";
import {
  Smile,      // Happy
  Frown,      // Sad
  Annoyed,    // Disgust
  Angry       // Anger
} from 'lucide-react';
import ImageIcon from '@mui/icons-material/Image';
import convertHeic from "../../helper-methods/convertHEIC";
import imageCompression from 'browser-image-compression';

const InputBoxComponent = ({ emotionStyles, inputEmotion, setInputEmotion, chatId, setMessages, fetchMessages, setSentMessage }) => {
  const currentUser = localStorage.getItem('userId');
  const currentUsername = localStorage.getItem('username');
  const [inputValue, setInputValue] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  //This is used for sending messages to the backend to translate from the hex value from emotionStyles to an emotion like "Happy"
  const getEmotionByColor = (colorValue) => {
    return Object.keys(emotionStyles).find(key => emotionStyles[key] === colorValue) || "Unknown";
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      let fileToCompress = file;

      // 1. If it's HEIC, convert it to a Blob first
      if (file.name.toLowerCase().endsWith(".heic")) {
        const fileUrl = URL.createObjectURL(file);
        const convertedUrl = await convertHeic(fileUrl);
        const response = await fetch(convertedUrl);
        const blob = await response.blob();

        // Prepare the converted blob for compression
        fileToCompress = new File([blob], file.name.replace(/\.heic$/i, ".jpg"), { type: "image/jpeg" });
        URL.revokeObjectURL(fileUrl);
        URL.revokeObjectURL(convertedUrl);
      }

      // 2. Compression Settings
      const options = {
        maxSizeMB: 0.5,          // Max file size is 500KB
        maxWidthOrHeight: 1024, // Resizes larger images to 1024px max
        useWebWorker: true,
        fileType: "image/jpeg"
      };

      // 3. Execute Compression
      const compressedBlob = await imageCompression(fileToCompress, options);

      // 4. Convert Blob back to File for your FormData
      const finalFile = new File([compressedBlob], fileToCompress.name, {
        type: "image/jpeg",
      });
      handleUpload(finalFile);
      setSentMessage(true);

    } catch (error) {
      console.error("Image processing failed:", error);
    }
  };

  const handleUpload = async (selectedFile) => {
    if (!selectedFile) return;

    try {
      const userId = localStorage.getItem('userId');
      const fileExtension = selectedFile.name.split(".").pop();

      const formData = new FormData();
      formData.append('Id', chatId);
      formData.append('Messages[0].UserId', currentUser);
      formData.append('Messages[0].Username', currentUsername);
      formData.append('Messages[0].Content', selectedFile.name);
      formData.append('Messages[0].Emotion', getEmotionByColor(inputEmotion));

      // Append the actual file binary data
      formData.append('Messages[0].imageData', selectedFile);


      // 3. Send the request
      const response = await fetch(apiUrlPrefix + "MongoDb/UploadMessagePicture", {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Invalid credentials or permissions.');
        }
        throw new Error('Network response was not ok');
      }
      //If it makes it to this point the response is OK
      const data = await response.json();
      await fetchMessages();
      return data;
    } catch (err) {
      console.error("Upload failed", err);
    }
  };



  //Sending the message itself

  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      // Prepare the message to send to the backend
      const newMessage = {
        userId: currentUser,
        username: currentUsername,
        content: inputValue,
        emotion: getEmotionByColor(inputEmotion)
      };

      // Optimistically add message to local state
      //setMessages((prevMessages) => [...prevMessages, newMessage]);

      // Send the message to the backend via POST request
      const bearerToken = localStorage.getItem('accessToken');
      try {
        const response = await fetch(apiUrlPrefix + "Discussion/sendMessage", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${bearerToken}`,
          },
          body: JSON.stringify({
            Id: chatId, // Pass the current discussion ID
            messages: [newMessage], // Send the new message as part of the request body
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to send the message");
        }

        // Clear the input field after sending
        setInputValue("");
        await fetchMessages();
        setSentMessage(true);

      } catch (error) {
        console.error("Error sending message:", error);
        // Roll back optimistic update on error
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => msg !== newMessage)
        );
      }
    }
  };

  return (
    <Box
      component={"form"}
      onSubmit={(e) => {
        e.preventDefault();
        handleSendMessage();
      }} sx={{ display: 'flex', gap: 1, paddingRight: 1, alignItems: 'center', justifyContent: 'center' }}>
      <TextField
        fullWidth
        variant="outlined"
        disabled={(chatId === '' || chatId === '69769ec2f601532a29c0a0a3') ? true : false}
        placeholder="Type a message..."
        slotProps={{
          input: {
            sx: {
              backgroundColor: inputEmotion || 'grey.200'
            }
          }
        }}
        sx={{ paddingLeft: 1 }}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />

      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="upload-photo-input"
        type="file"
        onChange={handleFileChange} // Just pass the function name, React handles the event
      />

      <label htmlFor="upload-photo-input" style={{ cursor: 'pointer' }}>
        <ImageIcon
          fontSize="large"
          sx={{
            color: 'blue',
            '&:hover': { color: 'darkblue' } // Optional: visual feedback
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </label>

      <EmojiComponent setInputValue={setInputValue} />
      {/* The Floating Menu */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          display: { xs: 'none', xl: 'flex' },
          position: 'relative',
          bottom: '0px', // Pushes it above the input bar
          left: '0px', // Adjust based on your Send button width
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '20px',
          boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
          zIndex: 10,
          border: '1px solid #ddd'
        }}
      >

        <Smile color="rgb(3, 3, 3)" size={28} onClick={() => setInputEmotion(emotionStyles.Neutral)} />
        <Smile color="#d1a90aff" size={28} style={{ cursor: 'pointer' }} onClick={() => setInputEmotion(emotionStyles.Happy)} /> {/* Happy */}
        <Frown color="#4A90E2" size={28} style={{ cursor: 'pointer' }} onClick={() => setInputEmotion(emotionStyles.Sad)} /> {/* Sad */}
        <Annoyed color="#2ECC71" size={28} style={{ cursor: 'pointer' }} onClick={() => setInputEmotion(emotionStyles.Annoyed)} /> {/* Annoyed */}
        <Angry color="#E74C3C" size={28} style={{ cursor: 'pointer' }} onClick={() => setInputEmotion(emotionStyles.Angry)} /> {/* Angry */}

      </Stack>


      <Button
        variant="contained"
        onMouseDown={(e) => {
          e.preventDefault();
          handleSendMessage();
        }}
        sx={{ minWidth: "100px" }}
      >
        Send
      </Button>
    </Box >
  )
};

export default InputBoxComponent;